import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import Modal from 'react-modal'
import * as S from './ModalProvider.styled'
import type { ModalContextType, ModalProviderProps, State } from './types'

export const ModalContext = createContext<ModalContextType>({
  open: () => {},
  close: () => {},
})

export const ModalProvider = ({ children, onModalOpen, onModalClose }: ModalProviderProps) => {
  const [modalState, setModalState] = useState<State>()

  const openModal: ModalContextType['open'] = useCallback(
    (component, props) => {
      setModalState({ component, props })

      if (onModalOpen) {
        onModalOpen()
      }
    },
    [setModalState, onModalOpen],
  )

  const closeModal = useCallback(() => {
    setModalState({ component: null, props: {} })

    if (onModalClose) {
      onModalClose()
    }
  }, [setModalState, onModalClose])

  const value: ModalContextType = useMemo(
    () => ({
      open: openModal,
      close: closeModal,
    }),
    [openModal, closeModal],
  )

  const renderModal = useCallback(
    ({ close }: ModalContextType) => {
      const Component = modalState?.component
      const props = modalState?.props || {}

      if (!Component) {
        return null
      }

      /**
       * Allows custom control over the `onRequestClose` prop.
       * By default, the modal closes when the Esc key is pressed.
       * To override this behavior, supply a custom `onRequestClose` function as a prop.
       *
       * Example usage:
       * open(ProductModal, {
       *   initialValues: {}, // Initial values for the modal form (customize as needed)
       *   onRequestClose: () => {
       *     close(); // Closes the modal programmatically
       *     console.log('Modal has been closed'); // Custom logic to run when the modal is closed with the Esc key
       *   }
       * });
       */

      return (
        <Modal
          isOpen={!!modalState?.component}
          overlayClassName="modal__overlay"
          htmlOpenClassName="ReactModal__Html--open"
          className="modal"
          aria={{
            labelledby: 'modal-box',
            describedby: 'modal-box',
          }}
          onRequestClose={props?.onRequestClose || close}
        >
          <Component close={close} {...props} />
        </Modal>
      )
    },
    [modalState],
  )

  return (
    <ModalContext.Provider value={value}>
      <S.Global />
      {children}
      <ModalContext.Consumer>{renderModal}</ModalContext.Consumer>
    </ModalContext.Provider>
  )
}

export const useModalContext = () => {
  return useContext(ModalContext)
}
